if (!customElements.get('nelko-sale-catalog')) {
  class NelkoSaleCatalog extends HTMLElement {
    connectedCallback() {
      this.links = [...this.querySelectorAll('[data-sale-link]')];
      this.regions = [...this.querySelectorAll('[data-sale-region]')];
      this.navigation = this.querySelector('[data-sale-navigation]');
      if (!this.navigation || !this.links.length) return;
      this.classList.toggle('nelko-sale-catalog--animated', true);
      this.style.setProperty('--sale-tab-count', this.links.length);

      this.controller = new AbortController();
      const { signal } = this.controller;
      this.addEventListener('click', (event) => this.onLinkClick(event), { signal });
      const schedule = () => {
        if (this.frame) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = null;
          this.update();
        });
      };
      window.addEventListener('scroll', () => {
        schedule();
        if (this.scrollTarget) this.scheduleScrollFinish(150);
      }, { passive: true, signal });
      window.addEventListener('scrollend', () => this.finishScroll(), { signal });
      const cancelScrollTarget = () => {
        this.scrollTarget = null;
        clearTimeout(this.scrollTimer);
        schedule();
      };
      window.addEventListener('wheel', cancelScrollTarget, { passive: true, signal });
      window.addEventListener('touchstart', cancelScrollTarget, { passive: true, signal });
      window.addEventListener('keydown', (event) => {
        if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key)) cancelScrollTarget();
      }, { signal });
      window.addEventListener('resize', schedule, { signal });
      document.addEventListener('shopify:block:select', (event) => {
        const region = this.regions.find((item) => item.dataset.blockId === event.detail.blockId || item.contains(event.target));
        if (region) this.scrollToRegion(region, false);
      }, { signal });
      this.resizeObserver = new ResizeObserver(schedule);
      this.resizeObserver.observe(this.navigation);
      this.regions.forEach((region) => this.resizeObserver.observe(region));
      this.update();
    }

    disconnectedCallback() {
      this.controller?.abort();
      this.resizeObserver?.disconnect();
      if (this.frame) cancelAnimationFrame(this.frame);
      clearTimeout(this.scrollTimer);
      this.scrollTarget = null;
    }

    headerOffset() {
      const header = document.querySelector('.section-header');
      if (!header || header.classList.contains('shopify-section-header-hidden')) return 0;
      const position = getComputedStyle(header).position;
      if (position !== 'sticky' && position !== 'fixed') return 0;
      return Math.max(0, header.getBoundingClientRect().bottom);
    }

    update() {
      this.offset = this.headerOffset();
      this.style.setProperty('--sale-header-offset', `${this.offset}px`);
      const boundary = this.offset;
      let active = this.regions[0];
      for (const region of this.regions) {
        // Allow for subpixel rounding at the scroll destination.
        if (region.getBoundingClientRect().top <= boundary + 2) active = region;
      }
      this.setActiveRegion(this.scrollTarget || active);
    }

    setActiveRegion(active) {
      this.style.setProperty('--sale-active-offset', `${this.regions.indexOf(active) * 100}%`);
      this.links.forEach((link) => {
        const selected = link.hash === `#${active.id}`;
        link.classList.toggle('is-active', selected);
        if (selected) link.setAttribute('aria-current', 'location');
        else link.removeAttribute('aria-current');
      });
    }

    onLinkClick(event) {
      const link = event.target.closest('[data-sale-link]');
      if (!link || event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      const region = this.regions.find((item) => `#${item.id}` === link.hash);
      if (!region) return;
      event.preventDefault();
      this.scrollToRegion(region, true);
      history.replaceState(null, '', link.hash);
    }

    scrollToRegion(region, focus) {
      this.update();
      this.scrollTarget = region;
      this.setActiveRegion(region);
      const top = region.getBoundingClientRect().top + window.scrollY - this.offset;
      window.scrollTo({ top: Math.max(0, top), behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth' });
      if (focus) region.focus({ preventScroll: true });
      this.scheduleScrollFinish(1000);
    }

    scheduleScrollFinish(delay) {
      clearTimeout(this.scrollTimer);
      this.scrollTimer = setTimeout(() => this.finishScroll(), delay);
    }

    finishScroll() {
      if (!this.scrollTarget) return;
      clearTimeout(this.scrollTimer);
      const region = this.scrollTarget;
      this.update();
      const top = Math.max(0, region.getBoundingClientRect().top + window.scrollY - this.offset);
      // The header can change height/visibility during smooth scrolling.
      if (Math.abs(top - window.scrollY) > 2) window.scrollTo({ top, behavior: 'instant' });
      this.scrollTarget = null;
      this.setActiveRegion(region);
    }
  }
  customElements.define('nelko-sale-catalog', NelkoSaleCatalog);
}
